/**
 * Question bank API service for AskAsper
 * Fetches question bank data bucketed by categories from the backend.
 */

import { ASK_ASPER_BASE_URL } from '../config';

export interface QuestionBankCategory {
    id: string;
    name: string;
    displayName: string;
    description: string;
    questions: Array<{
        id: string;
        text: string;
    }>;
}

/** Design library expects { Category, Questions: [{ QuestionID, Question }] } */
export interface QuestionBankCategoryApiFormat {
    Category: string;
    Questions: Array<{ QuestionID: number; Question: string }>;
}

/** Convert our format to API format for asper_design_library */
export function toQuestionBankApiFormat(
    categories: QuestionBankCategory[],
): QuestionBankCategoryApiFormat[] {
    return categories.map((c) => ({
        Category: c.displayName,
        Questions: c.questions.map((q) => ({
            QuestionID: parseInt(q.id, 10) || 0,
            Question: q.text,
        })),
    }));
}

/**
 * API response format from the backend
 */
interface QuestionBankAPIResponse {
    Category: string;
    Questions: Array<{
        QuestionID: number;
        Question: string;
    }>;
}

function getAuthHeader(): string {
    const token = localStorage.getItem('accessToken');
    return `Bearer ${token}`;
}

/**
 * Get question bank data grouped by category
 */
export async function getQuestionBankData(): Promise<QuestionBankCategory[]> {
    const response = await fetch(`${ASK_ASPER_BASE_URL}question-bank-data`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: getAuthHeader(),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('Content-Type');
    if (contentType && contentType.includes('application/json')) {
        const raw = await response.json();
        const data = Array.isArray(raw) ? raw : raw?.data ?? raw?.categories ?? [];

        if (!data || !Array.isArray(data)) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('[QuestionBank] API returned unexpected format:', { raw });
            }
            return [];
        }

        const result = data
            .map((category: Record<string, unknown>) => {
                // Support both PascalCase (API) and camelCase (some backends)
                const categoryName =
                    (category.Category as string) ?? (category.category as string) ?? '';
                const questionsRaw =
                    (category.Questions as Array<{ QuestionID?: number; Question?: string; questionId?: number; question?: string }>) ??
                    (category.questions as Array<{ QuestionID?: number; Question?: string; questionId?: number; question?: string }>) ??
                    [];

                if (!categoryName) return null;

                const questions = Array.isArray(questionsRaw)
                    ? questionsRaw
                        .map((q) => ({
                            id: String(q.QuestionID ?? q.questionId ?? ''),
                            text: String(q.Question ?? q.question ?? ''),
                        }))
                        .filter((q) => q.text)
                    : [];

                return {
                    id: categoryName.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and'),
                    name: categoryName.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and'),
                    displayName: categoryName,
                    description: categoryName,
                    questions,
                };
            })
            .filter((c): c is QuestionBankCategory => c != null);

        if (process.env.NODE_ENV === 'development' && result.length === 0 && data.length > 0) {
            console.warn('[QuestionBank] No valid categories after transform. Raw sample:', data[0]);
        }

        return result;
    }

    return [];
}
